/**
 * Achievement registry. Single source of truth for the 8 tournament-statistics
 * badges that show up on the Statistics page and on each TeamCard.
 *
 * Keys mirror the TournamentStatistics field names so the same key can be used
 * on the server (when persisting holders / firing notifications) and on the
 * client (for UI lookups and i18n via tournamentStatistics.summary.{key}).
 *
 * Color tokens come from the medieval palette in tailwind.config.js.
 */

export const ACHIEVEMENT_KEYS = [
  "mostChallenged",
  "topChallenger",
  "mostDefended",
  "mostOwnChallengeWon",
  "topBraveheartDecliner",
  "mostCardsUsed",
  "mostTightWins",
  "mostGamesPlayed",
  "mostMatchesPlayed",
] as const;

export type AchievementKey = (typeof ACHIEVEMENT_KEYS)[number];

export interface AchievementMeta {
  /** Iconify icon ID rendered in the badge chip. */
  icon: string;
  /**
   * Tailwind palette token without prefix (e.g. "crown" → text-crown,
   * bg-crown/10). Always paired with the `/10` background opacity.
   */
  color: "crown" | "triumph" | "quest" | "herald" | "peril" | "valor" | "mystic";
}

export const ACHIEVEMENTS: Record<AchievementKey, AchievementMeta> = {
  mostChallenged: { icon: "lucide:target", color: "quest" },
  topChallenger: { icon: "lucide:swords", color: "crown" },
  mostDefended: { icon: "lucide:shield-check", color: "triumph" },
  mostOwnChallengeWon: { icon: "lucide:trophy", color: "crown" },
  topBraveheartDecliner: { icon: "game-icons:chicken", color: "valor" },
  mostCardsUsed: { icon: "lucide:sparkles", color: "mystic" },
  mostTightWins: { icon: "lucide:flame", color: "peril" },
  mostGamesPlayed: { icon: "lucide:activity", color: "herald" },
  mostMatchesPlayed: { icon: "lucide:calendar-check", color: "triumph" },
};

/**
 * Tailwind class strings for the badge chip. Pre-baked so the JIT compiler
 * actually sees concrete class names instead of dynamic `bg-${color}/10`.
 */
export const ACHIEVEMENT_CHIP_CLASSES: Record<
  AchievementMeta["color"],
  { bg: string; text: string }
> = {
  crown: { bg: "bg-crown/15", text: "text-crown" },
  triumph: { bg: "bg-triumph/15", text: "text-triumph" },
  quest: { bg: "bg-quest/15", text: "text-quest" },
  herald: { bg: "bg-herald/15", text: "text-herald" },
  peril: { bg: "bg-peril/15", text: "text-peril" },
  valor: { bg: "bg-valor/15", text: "text-valor" },
  mystic: { bg: "bg-mystic/15", text: "text-mystic" },
};

export function getAchievementChipClasses(key: AchievementKey): {
  bg: string;
  text: string;
} {
  return ACHIEVEMENT_CHIP_CLASSES[ACHIEVEMENTS[key].color];
}
